import { View } from "@westpierstudio/luma/mvc";
import { ExternalItem } from "@westpierstudio/luma/utils/elastic-cache";
import { GameServices } from "game/GameServices";
import { Rectangle } from "pixi.js";
import { SymbolID } from "servertypes/SharedTypes";
import { MultiplierBadge } from "../multiplierbadges/MultiplierBadge";
import { SymbolCache } from "./Reels";
import { Spine } from "pixi-spine";
import { resetSpine } from "utils/Misc";
import { Signal } from "type-signals";

class ReelSymbolSignals {
    scatterWinComplete = new Signal();
    topSymbolInteract = new Signal();
}

export class ReelSymbol extends View {
    private spine?: ExternalItem<Spine>;
    private multiplierBadge?: MultiplierBadge;
    private _id: SymbolID = `F6`;
    signals = new ReelSymbolSignals();

    constructor(protected override readonly services: GameServices, setupMultiplier: boolean,
        private readonly symbolCache: SymbolCache) {
        super(services);

        this.addListener(`click`, this.onInteract, this)
        .addListener(`tap`, this.onInteract, this);

        const {symbolSize} = services.resources.getSpec(`reels`);
        const {width, height} = symbolSize;
        this.hitArea = new Rectangle(-width / 2, -height / 2, width, height);

        if (setupMultiplier) {

            this.multiplierBadge = new MultiplierBadge(this.services);
            this.addChild(this.multiplierBadge);
        }
    }

    get id() {
        return this._id;
    }

    setSymbol(id: SymbolID, useWildBonus: boolean, multiplier?: number): void {

        if (id === `B1` && useWildBonus) {
            id = `B1W`;
        }

        if (this._id !== id || this.spine === undefined) {

            if (this.spine) {

                this.removeChild(this.spine.reference);
                this.symbolCache[this._id].free(this.spine);
            }

            this._id = id;
            this.spine = this.symbolCache[this._id].get();
            this.addChildAt(this.spine.reference, 0);
        }

        this.setAnimStaticState();

        if (this.multiplierBadge) {

            if (multiplier) {
                this.multiplierBadge.setValue(multiplier);
                this.multiplierBadge.visible = true;
            } else {
                this.multiplierBadge.visible = false;
            }
        }
    }

    startWinAnimLoop(): void {

        this.spine?.reference.state.setAnimation(0, (this._id === `B1W`) ? `wild-bonus-win` : `win`, true);

        //Hide symbol multi badge instances as win animations happen in the overlay layer
        if (this.multiplierBadge && this.multiplierBadge.visible) {
            this.multiplierBadge.spine.visible = false;
        }
    }

    scatterWinSequence(): void {

        if (this.id === `B1` || this.id === `B1W`) {

            const animName = (this.id === `B1`) ? `bonus-intro` : `wild-bonus-intro`;

            this.spine?.reference.state.setAnimation(0, animName, false);
            this.spine?.reference.state.addListener({ complete: () => {

                this.spine?.reference.state.clearListeners();
                this.scatterWinLoop();
                this.signals.scatterWinComplete.dispatch();
            }});

        }
    }

    scatterWinLoop(): void {

        if (this.id === `B1` || this.id === `B1W`) {

            const animName = (this.id === `B1`) ? `bonus-loop` : `wild-bonus-loop`;

            this.spine?.reference.state.setAnimation(0, animName, true);
            this.spine?.reference.state.addListener({ complete: () => {

                this.spine?.reference.state.clearListeners();
                this.signals.scatterWinComplete.dispatch();
            }});
        }
    }

    stopWinAnimLoop(): void {

        this.setAnimStaticState();

        if (this.multiplierBadge && this.multiplierBadge.visible) {
            this.multiplierBadge.spine.visible = true;
        }
    }

    playLandAnim(): void {

        if (this._id === `B1`) {
            this.spine?.reference.state.setAnimation(0, `bonus-land`, false);

        } else if (this._id === `B1W`) {
            this.spine?.reference.state.setAnimation(0, `wild-bonus-land`, false);
        }
    }

    playMultiBadgeLandAnim(): void {

        if (this.multiplierBadge && this.multiplierBadge.visible) {
            this.multiplierBadge.playLandAnim();
        }
    }

    setInteractive(interactive: boolean) {
        this.interactive = this.buttonMode = interactive;
    }

    override reset(): void {

        if (this.spine) {
            resetSpine(this.spine?.reference);
        }

        if (this.multiplierBadge) {
            this.multiplierBadge.reset();
        }
    }

    private setAnimStaticState(): void {

        if (this.spine) {

            resetSpine(this.spine.reference);

            let anim;

            if (this._id === `B1`) {
                anim = `bonus-static`;

            } else if (this._id === `B1W`) {
                anim = `wild-bonus-static`;

            } else {
                anim = `static`;
            }

            this.spine.reference.state.setAnimation(0, anim, false);
        }
    }

    private onInteract(): void {
        this.signals.topSymbolInteract.dispatch();
    }
}